//
// studentEmailVerdict
//
// Turns the server's verdict on a student email address into the message the
// teacher sees, for the three forms that ask: adding one student, adding
// several, and editing an existing student's email.
//
// PE-1807: all three used to show the same sentence for every outcome, and all
// three blocked whenever any account held the email. That made it impossible to
// re-add a student who had been removed, because removal never deletes the
// account.
//
import { StudentEmailVerdict } from '@peekapak/types/DataProtocolTypes';

export const EMAIL_CHECK_FAILED_ERROR = "We couldn't verify this email right now. Please try again.";

const OUT_OF_SCOPE_ERROR =
  'This email belongs to an account you do not manage. Please contact support if you think this is a mistake.';

const NOT_A_STUDENT_ERROR = 'This email belongs to an educator account and cannot be used for a student.';

const INCONSISTENT_ERROR =
  'The account for this email needs to be repaired before it can be used. Please contact support.';

// Adding a student may reuse an intact account the caller manages: that is the
// restore case. Editing a student's email may not, because pointing one profile
// at an address another account already holds would strand the account that
// holds it today, along with the child's journals and assignments.
export type EmailUsage = 'add' | 'edit';

// The teacher hit this by creating a name-only student and then trying to give
// it the email of a student she had removed earlier, which is the natural way
// to move a student between classes. Refusing is right, because letting the new
// empty account take the address would orphan the real one. But the refusal has
// to say what to do instead, or it is a dead end.
//
// Deliberately does not say "removed": REUSABLE also covers a student of hers
// who is still enrolled in another class, and the advice is the same either way.
const REUSABLE_ON_EDIT_ERROR =
  'This email belongs to one of your existing students. To put that student in this class, add them to the class using this email address rather than creating a new student here.';

// Returns the error to show, or null when the email may be used.
export function getStudentEmailVerdictError(verdict: StudentEmailVerdict, usage: EmailUsage): string | null {
  switch (verdict) {
    case StudentEmailVerdict.AVAILABLE:
      return null;
    case StudentEmailVerdict.REUSABLE:
      return usage === 'add' ? null : REUSABLE_ON_EDIT_ERROR;
    case StudentEmailVerdict.NOT_A_STUDENT:
      return NOT_A_STUDENT_ERROR;
    case StudentEmailVerdict.INCONSISTENT:
      return INCONSISTENT_ERROR;
    case StudentEmailVerdict.OUT_OF_SCOPE:
      return OUT_OF_SCOPE_ERROR;
    default:
      // An unrecognized verdict blocks. A check that could not be understood is
      // never evidence that the email is free.
      return EMAIL_CHECK_FAILED_ERROR;
  }
}

export function isRestoringExistingAccount(verdict: StudentEmailVerdict): boolean {
  return verdict === StudentEmailVerdict.REUSABLE;
}

export const DUPLICATE_EMAIL_ERROR = 'This email address is used more than once. Each student needs their own.';

// Which error to show for one address in a batch, given how many rows carry it.
//
// A blocking verdict outranks a repeat: it explains why the address cannot be
// used at all, which is more useful than being told it appears twice. With no
// blocking verdict, a repeat is still an error, because both rows resolve to a
// single account and the second would silently restore what the first created.
export function getBatchEmailError(verdictError: string | null, occurrences: number): string | null {
  if (verdictError) {
    return verdictError;
  }

  return occurrences > 1 ? DUPLICATE_EMAIL_ERROR : null;
}
